import api from './api';

// Description: Submit beta access form
// Endpoint: POST /api/forms/beta
// Request: { email: string, jobRole: string, organizationName: string, monthlyBudget: string, challenge: string }
// Response: { success: boolean, message: string, submissionId?: string, warning?: string }
export const submitBetaForm = async (data: { email: string; jobRole: string; organizationName: string; monthlyBudget: string; challenge: string; stageid: string }) => {
  try {
    const response = await api.post('/api/forms/beta', data);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(error?.response?.data?.error || error.message);
  }
}

// Description: Submit demo request form
// Endpoint: POST /api/forms/demo
// Request: { firstName: string, lastName: string, email: string, phone: string, organizationName: string, organizationType: string, teamSize: string, preferredDate: string, preferredTime: string, interest: string, additionalInfo?: string }
// Response: { success: boolean, message: string, submissionId?: string }
export const submitDemoForm = async (data: { firstName: string; lastName: string; email: string; phone: string; organizationName: string; organizationType: string; teamSize: string; preferredDate: string; preferredTime: string; interest: string; additionalInfo?: string }) => {
  try {
    const response = await api.post('/api/forms/demo', data);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(error?.response?.data?.error || error.message);
  }
}

// Description: Submit contact form
// Endpoint: POST /api/forms/contact
// Request: { name: string, email: string, company: string, subject: string, message: string }
// Response: { success: boolean, message: string, submissionId?: string }
export const submitContactForm = async (data: { name: string; email: string; company: string; subject: string; message: string }) => {
  try {
    const response = await api.post('/api/forms/contact', data);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(error?.response?.data?.error || error.message);
  }
}

// Description: Submit integration request form
// Endpoint: POST /api/forms/request
// Request: { integrationName: string, companyName: string, email: string, priorityLevel: string, useCase: string }
// Response: { success: boolean, message: string, submissionId?: string, giteaIssueId?: number, giteaIssueUrl?: string, warning?: string }
export const submitRequestForm = async (data: { integrationName: string; companyName: string; email: string; priorityLevel: string; useCase: string }) => {
  try {
    const response = await api.post('/api/forms/request', data);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(error?.response?.data?.error || error.message);
  }
}

// Description: Submit careers application
// Endpoint: POST /api/forms/careers
// Request: { firstName: string, lastName: string, email: string, ... (all career form fields) }
// Response: { success: boolean, message: string, applicationId?: string }
export const submitCareersApplication = async (data: any) => {
  console.log('submitCareersApplication called with data:', data);
  console.log('API base URL:', api.defaults.baseURL);
  console.log('Current window location:', window.location.href);
  
  try {
    console.log('Making API request to /api/forms/careers');
    const response = await api.post('/api/forms/careers', data);
    console.log('API response received:', response);
    return response.data;
  } catch (error: any) {
    console.error('API error details:', {
      message: error.message,
      code: error.code,
      config: error.config,
      request: error.request,
      response: error.response
    });
    
    if (error.code === 'ECONNREFUSED') {
      throw new Error('Unable to connect to server. Please ensure the backend is running on port 3000.');
    }
    
    throw new Error(error?.response?.data?.error || error.message);
  }
}

// Description: Get all contact form submissions
// Endpoint: GET /api/forms/contact
// Request: {}
// Response: { success: boolean, message: string, data: Array<ContactSubmission>, total: number }
export const getContactSubmissions = async () => {
  try {
    const response = await api.get('/api/forms/contact');
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(error?.response?.data?.error || error.message);
  }
};

// Description: Get all integration request submissions
// Endpoint: GET /api/forms/request
// Request: { limit?: number, skip?: number }
// Response: { success: boolean, message: string, data: Array<IntegrationRequest>, total: number }
export const getIntegrationRequests = async (params?: { limit?: number; skip?: number }) => {
  try {
    const response = await api.get('/api/forms/request', { params });
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(error?.response?.data?.error || error.message);
  }
};

// Type definition for contact submission
export interface ContactSubmission {
  _id: string;
  type: string;
  ipAddress: string;
  email: string;
  name: string;
  company: string;
  subject: string;
  message: string;
  status: string;
  emailSent: boolean;
  adminNotified: boolean;
  createdAt: string;
  updatedAt: string;
}

// Type definition for integration request submission
export interface IntegrationRequest {
  _id: string;
  type: string;
  ipAddress: string;
  integrationName: string;
  companyName: string;
  email: string;
  priorityLevel: string;
  useCase: string;
  giteaIssueId?: number;
  giteaIssueUrl?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}